import { z } from 'zod';
import { Confidence } from './entities.js';
import { ThreadSubject, SurfaceState } from './chat-action.js';
/**
 * `threadId` and `subject` are optional for back-compat — a /api/ask call
 * with neither runs as a one-shot single-turn query (no persistence). When
 * `threadId` is present the server reads prior turns from Firestore and
 * folds them into the model's `messages` array natively. When `subject` is
 * present the server adds a "Surface context" block to the system prompt
 * so the assistant knows what view the analyst is looking at.
 *
 * `surfaceState` is the transient view state the analyst has built up
 * (filters, hides, pins, selection). It feeds the same Surface-context
 * block but is NOT persisted to the thread doc — re-opening an old
 * thread shouldn't re-apply old filters.
 */
export const AskRequest = z.object({
    question: z.string().min(1),
    maxHops: z.number().int().min(1).max(4).default(2),
    threadId: z.string().optional(),
    subject: ThreadSubject.optional(),
    surfaceState: SurfaceState.optional(),
});
/**
 * `retrievalPath` records how the document made it onto the source list:
 *   - 'graph'    — found via entity → MENTIONS edge traversal
 *   - 'semantic' — found by cosine top-K against the question embedding
 *   - 'both'     — found by both paths (highest-trust signal)
 *
 * Optional for forward-compat with older clients/callers.
 */
export const SourceRef = z.object({
    id: z.string(),
    title: z.string().optional(),
    excerpt: z.string().optional(),
    sourceType: z.string().optional(),
    year: z.number().optional(),
    sourceLang: z.enum(['en', 'fa', 'other']).optional(),
    retrievalPath: z.enum(['graph', 'semantic', 'both']).optional(),
});
/**
 * Temporal range for a relationship. `end` undefined means "still current".
 * Product spec §6.4: "Parsi was president of NIAC 2002–2018" is a single
 * versioned edge rather than a replacement of a prior edge.
 */
export const Validity = z.object({
    start: z.number().int().optional(),
    end: z.number().int().optional(),
});
export const Fact = z.object({
    direction: z.enum(['outgoing', 'incoming']),
    subject: z.string(),
    predicate: z.string(),
    object: z.string(),
    props: z.record(z.string(), z.unknown()).default({}),
    confidence: Confidence.optional(),
    validity: Validity.optional(),
});
/**
 * Inline citation anchor used in synthesized answers.
 * Referenced from the text by `[ID]`. Enriched with source metadata and
 * confidence for the <Citation> component's hover preview.
 */
export const CitationRef = z.object({
    id: z.string(),
    supportingQuote: z.string().optional(),
    confidence: Confidence.optional(),
});
export const AskResult = z.object({
    answer: z.string(),
    entities: z.array(z.string()),
    factCount: z.number().int().nonnegative(),
    sources: z.array(SourceRef),
});
